/**
 * Highlight badges for template and theme cards (gallery, wizard). They are kept scarce so they
 * stay meaningful: "new" lasts NEW_FOR_DAYS after `added`, and the API only sends `popularRank` /
 * `lovedRank` for the top few items that clear a minimum count.
 */
export const CATALOG_BADGES = ["new", "popular", "loved", "featured"] as const;
export type CatalogBadge = (typeof CATALOG_BADGES)[number];

export const CATALOG_BADGE_LABELS: Record<CatalogBadge, string> = {
  new: "Baru",
  popular: "Terpopuler",
  loved: "Favorit",
  featured: "Pilihan",
};

/** Longer wording for tooltips and screen readers. */
export const CATALOG_BADGE_HINTS: Record<CatalogBadge, string> = {
  new: "Baru ditambahkan",
  popular: "Paling banyak dipakai",
  loved: "Paling banyak difavoritkan",
  featured: "Pilihan tim Baze",
};

export const NEW_FOR_DAYS = 30;

export interface BadgeSignals {
  added?: string;
  featured?: boolean;
  /** 1 = most used. Present only for the top items (see the API catalog service). */
  popularRank?: number;
  /** 1 = most favourited. Present only for the top items. */
  lovedRank?: number;
}

const DAY = 86_400_000;

export function isNew(item: Pick<BadgeSignals, "added">, now: Date = new Date()): boolean {
  if (!item.added) return false;
  const age = (now.getTime() - Date.parse(`${item.added}T00:00:00Z`)) / DAY;
  return age >= 0 && age < NEW_FOR_DAYS;
}

/** Badges in display order. Cards show the first one or two. */
export function catalogBadges(item: BadgeSignals, now: Date = new Date()): CatalogBadge[] {
  const out: CatalogBadge[] = [];
  if (isNew(item, now)) out.push("new");
  if (item.popularRank) out.push("popular");
  if (item.lovedRank) out.push("loved");
  if (item.featured) out.push("featured");
  return out;
}

/** Order for a badge filter: newest first, or by rank. Items without the signal sort last. */
export function badgeOrder(item: BadgeSignals, badge: CatalogBadge): number {
  switch (badge) {
    case "new":
      return item.added ? -Date.parse(`${item.added}T00:00:00Z`) / DAY : Infinity;
    case "popular":
      return item.popularRank ?? Infinity;
    case "loved":
      return item.lovedRank ?? Infinity;
    default:
      return item.featured ? 0 : Infinity;
  }
}
